import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import EventAccordion, { chatMenuKey, eventMenuKey } from "../EventAccordion/EventAccordion";
import { IAdminVendorChatDetails, IChat, IChatsData, IChatsRecord } from "../../../../interfaces/chats";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { SendbirdUIKitProvider } from "../../../SendBird/SendbirdUIKitProvider";
import { formatMessageTimestamp } from "../../../../utils/formatTime";
import GroupChannelHandler from "@sendbird/uikit-react/handlers/GroupChannelHandler";
import { isObjectEmpty } from "../../../../helpers/common";
import { isSendbirdSessionExpired } from "../../../../helpers/sendbird";
import { getLoggedInVendorDisplayName, getSendbirdInboxSnippet } from "../../../../helpers/sendbirdDisplay";
import useUserInfo from "../../../../hooks/reduxHelpersHooks/useUserInfo";
import { AdminVendorChatItem } from "../ChatItem/ChatItem";
import { getAdminVendorChatRecordThunk } from "../../../../store/chats/chatsThunk";
import { fetchTeamMemberSendbirdSessionThunk } from "../../../../store/user/userThunk";
import { ROUTE_PATHS } from "../../../../routes";
import {
	fetchChannelMentionFlags,
	getChannelUnreadMentionCount,
} from "../../../../utils/sendbird/channelMentionStatus";
import { applyMentionFlagsToChatsRecord } from "../../../../utils/sendbird/chatAccessibility";
import { clearScheduledDebounced, scheduleDebounced } from "../../../../utils/sendbird/scheduleDebounced";
import {
	getGroupChannelLastMessage,
	getGroupChannelUrl,
	getMessageSenderUserId,
} from "../../../../utils/sendbird/sendbirdChannelUtils";
import ChatContextMenu, { ChatMenuAnchor, iChatMenuItem } from "../ChatContextMenu/ChatContextMenu";
import ArchivedEmptyState from "./ArchivedEmptyState";
import { EventChatsSkeleton } from "../MessagingSkeleton/MessagingSkeleton";
import { clearSelectedChatUnread, mergeApiEventsWithLocalPreviews } from "../../../../utils/messaging/inboxMerge";
import "./chatsList.scss";

const SUPPORT_MENU_KEY = "support";

const moveChannelEventToTop = (events: IChatsRecord[], channelUrl: string): IChatsRecord[] => {
	const index = events.findIndex((event) => event.chats.some((chat) => chat.channelUrl === channelUrl));
	if (index <= 0) return events;
	return [events[index], ...events.slice(0, index), ...events.slice(index + 1)];
};

type OpenMenu = { key: string; anchor: ChatMenuAnchor; items: iChatMenuItem[] };

type ListMenuProps = {
	archived?: boolean;
	openMenuKey: string | null;
	onEventMenu?: (event: IChatsRecord, anchor: ChatMenuAnchor) => void;
	onChatMenu?: (event: IChatsRecord, chat: IChat, anchor: ChatMenuAnchor) => void;
};

interface ChatsListProps {
	chatsRecord: IChatsData | null;
	expandedEvents: Set<string>;
	selectedChatId?: string;
	loading: boolean;
	hasMore: boolean;
	currentPage: number;
	filtersResetKey?: string;
	onAccordionToggle: (eventId: string) => void;
	onChatClick: (eventId: string, chatId: string) => void;
	onLoadMore: (page: number) => void;
	handleGetUnreadChannelCount: (fetchChatsRecord?: boolean) => void;
	onSendbirdHandledUpdate?: () => void;
	archived?: boolean;
	getEventMenuItems?: (event: IChatsRecord) => iChatMenuItem[];
	getChatMenuItems?: (event: IChatsRecord, chat: IChat) => iChatMenuItem[];
	getSupportMenuItems?: () => iChatMenuItem[];
	archivedRow?: React.ReactNode;
}

const ChatsList: React.FC<ChatsListProps> = ({
	chatsRecord,
	expandedEvents,
	onAccordionToggle,
	selectedChatId,
	onChatClick,
	loading,
	hasMore,
	currentPage,
	onLoadMore,
	filtersResetKey = "",
	handleGetUnreadChannelCount,
	onSendbirdHandledUpdate,
	archived,
	getEventMenuItems,
	getChatMenuItems,
	getSupportMenuItems,
	archivedRow,
}) => {
	const events = chatsRecord?.events || [];
	const { adminVendorChatRecord, apiError } = useAppSelector((state) => state.chats);
	const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);

	const onEventMenu = useMemo(
		() =>
			getEventMenuItems
				? (event: IChatsRecord, anchor: ChatMenuAnchor) => {
						const items = getEventMenuItems(event);
						if (items.length) setOpenMenu({ key: eventMenuKey(event.eventId), anchor, items });
					}
				: undefined,
		[getEventMenuItems],
	);

	const onChatMenu = useMemo(
		() =>
			getChatMenuItems
				? (event: IChatsRecord, chat: IChat, anchor: ChatMenuAnchor) => {
						const items = getChatMenuItems(event, chat);
						if (items.length) setOpenMenu({ key: chatMenuKey(chat.id), anchor, items });
					}
				: undefined,
		[getChatMenuItems],
	);

	const onSupportMenu = useMemo(
		() =>
			getSupportMenuItems
				? (anchor: ChatMenuAnchor) => {
						const items = getSupportMenuItems();
						if (items.length) setOpenMenu({ key: SUPPORT_MENU_KEY, anchor, items });
					}
				: undefined,
		[getSupportMenuItems],
	);

	const menuProps: ListMenuProps = { archived, openMenuKey: openMenu?.key ?? null, onEventMenu, onChatMenu };
	const supportMenuProps = {
		isMenuOpen: openMenu?.key === SUPPORT_MENU_KEY,
		onOpenMenu: adminVendorChatRecord?.supportChannelUrl ? onSupportMenu : undefined,
	};
	const showSupportChat = !archived;
	const {
		teamMemberSendbirdSession,
		isTeamMemberSendbirdTokenLoading,
		teamMemberSendbirdApiError,
	} = useAppSelector((state) => state.user);

	// Calculate chat index offset for each event to maintain color cycling
	const chatIndexOffsets = useMemo(() => {
		const offsets: number[] = [];
		let offset = 0;
		events.forEach((event) => {
			offsets.push(offset);
			offset += event.chats.length;
		});
		return offsets;
	}, [events]);

	// Handle load more
	const handleNext = useCallback(() => {
		if (!loading && hasMore) {
			const nextPage = currentPage + 1;
			onLoadMore(nextPage);
		}
	}, [loading, hasMore, currentPage, onLoadMore]);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const isFirstMount = useRef(true);
	const dispatch = useAppDispatch();

	useEffect(() => {
		void dispatch(getAdminVendorChatRecordThunk());
	}, [dispatch]);

	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false;
			return;
		}
		scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	}, [filtersResetKey]);

	const eventsListUikitOptions = useMemo(
		() => ({
			groupChannel: {
				enableVoiceMessage: true,
				enableMention: true,
				typingIndicatorTypes: new Set(["bubble"] as const),
			},
		}),
		[],
	);

	const isTeamMemberSessionValid =
		!!teamMemberSendbirdSession?.accessToken &&
		!isSendbirdSessionExpired(teamMemberSendbirdSession.expiresAt);

	const eventsListMode = useMemo((): "live" | "loading" | "degraded" | "none" => {
		if (events.length === 0) return "none";
		if (isTeamMemberSessionValid && !isTeamMemberSendbirdTokenLoading) return "live";
		if (isTeamMemberSendbirdTokenLoading) return "loading";
		return "degraded";
	}, [events.length, isTeamMemberSessionValid, isTeamMemberSendbirdTokenLoading]);

	const handleRetrySendbirdSession = useCallback(() => {
		void dispatch(fetchTeamMemberSendbirdSessionThunk({ force: true }));
	}, [dispatch]);

	const isWaitingForInitialChats = chatsRecord === null && (loading || !apiError);

	const hasLegacyAdminSupport =
		!!adminVendorChatRecord?.supportChannelUrl &&
		adminVendorChatRecord.isSupportSendbirdNewModel !== true;

	const renderEventsSectionBody = (mode: "live" | "loading" | "degraded" | "none") => (
		<>
			{isWaitingForInitialChats || mode === "loading" ? (
				<EventChatsSkeleton />
			) : events.length === 0 && !loading ? (
				archived ? (
					showSupportChat ? null : <ArchivedEmptyState />
				) : (
					<Box className="emptyState emptyStateInline">
						<Typography className="emptyStateTitle">No results found</Typography>
						<Typography className="emptyStateDescription">
							No conversations match your search or filters.
						</Typography>
					</Box>
				)
			) : null}

			{mode === "degraded" ? (
				<>
					<ChatsListDegradedBanner
						errorMessage={teamMemberSendbirdApiError?.message}
						onRetry={handleRetrySendbirdSession}
					/>
					<RenderStaticList
						chatIndexOffsets={chatIndexOffsets}
						handleNext={handleNext}
						events={events}
						hasMore={hasMore}
						expandedEvents={expandedEvents}
						onAccordionToggle={onAccordionToggle}
						selectedChatId={selectedChatId}
						onChatClick={onChatClick}
						{...menuProps}
					/>
				</>
			) : null}

			{mode === "live" ? (
				<RenderList
					chatIndexOffsets={chatIndexOffsets}
					handleNext={handleNext}
					events={events}
					hasMore={hasMore}
					expandedEvents={expandedEvents}
					onAccordionToggle={onAccordionToggle}
					selectedChatId={selectedChatId}
					onChatClick={onChatClick}
					handleGetUnreadChannelCount={handleGetUnreadChannelCount}
					onSendbirdHandledUpdate={onSendbirdHandledUpdate}
					{...menuProps}
				/>
			) : null}
		</>
	);

	const renderSupportChannel = (insideTeamMemberProvider: boolean) => {
		if (!showSupportChat) return null;
		if (!adminVendorChatRecord?.supportChannelUrl) {
			return <AdminVendorChatItem handleSelect={() => {}} />;
		}

		const adminChat = (
			<RenderAdminChat
				adminVendorChatRecord={adminVendorChatRecord as IAdminVendorChatDetails}
				{...supportMenuProps}
			/>
		);

		if (insideTeamMemberProvider && hasLegacyAdminSupport) {
			return (
				<SendbirdUIKitProvider
					compact
					isNewModelChannel={false}
					vendorSendBirdId={adminVendorChatRecord.vendorSendBirdId}
					channelUrl={adminVendorChatRecord.supportChannelUrl}
				>
					{adminChat}
				</SendbirdUIKitProvider>
			);
		}

		if (!insideTeamMemberProvider) {
			return (
				<SendbirdUIKitProvider
					compact
					isNewModelChannel={adminVendorChatRecord.isSupportSendbirdNewModel === true}
					vendorSendBirdId={adminVendorChatRecord.vendorSendBirdId}
					channelUrl={adminVendorChatRecord.supportChannelUrl}
				>
					{adminChat}
				</SendbirdUIKitProvider>
			);
		}

		return adminChat;
	};

	return (
		<Box ref={scrollContainerRef} className="chatsListContent" id="chatsScrollableContainer">
			{eventsListMode === "live" ? (
				<SendbirdUIKitProvider
					className="chatsListSendbirdProvider"
					isNewModelChannel
					uikitOptions={eventsListUikitOptions}
				>
					<Box className="chatsListSupportChannel">{renderSupportChannel(true)}</Box>
					{archivedRow}
					<Box className="chatsListEventsSection">{renderEventsSectionBody("live")}</Box>
				</SendbirdUIKitProvider>
			) : (
				<>
					<Box className="chatsListSupportChannel">{renderSupportChannel(false)}</Box>
					{archivedRow}
					<Box className="chatsListEventsSection">{renderEventsSectionBody(eventsListMode)}</Box>
				</>
			)}
			<ChatContextMenu anchor={openMenu?.anchor ?? null} items={openMenu?.items ?? []} onClose={() => setOpenMenu(null)} />
		</Box>
	);
};

type EventsListBaseProps = ListMenuProps & {
	chatIndexOffsets: number[];
	handleNext: () => void;
	events: IChatsRecord[];
	hasMore: boolean;
	expandedEvents: Set<string>;
	selectedChatId?: string;
	onAccordionToggle: (eventId: string) => void;
	onChatClick: (eventId: string, chatId: string) => void;
};

const ChatsListDegradedBanner = ({
	errorMessage,
	onRetry,
}: {
	errorMessage?: string;
	onRetry: () => void;
}) => (
	<Box className="chatsListDegradedBanner">
		<Typography className="chatsListDegradedBannerText">
			{errorMessage
				? `Live chat updates are unavailable: ${errorMessage}`
				: "Live chat updates are temporarily unavailable."}
		</Typography>
		<Button size="small" variant="outlined" onClick={onRetry}>
			Retry connection
		</Button>
	</Box>
);

const RenderStaticList = ({
	chatIndexOffsets,
	handleNext,
	events,
	hasMore,
	expandedEvents,
	onAccordionToggle,
	selectedChatId,
	onChatClick,
	...menuProps
}: EventsListBaseProps) => (
	<InfiniteScroll
		dataLength={events.length}
		next={handleNext}
		hasMore={hasMore}
		loader={<EventChatsSkeleton events={1} />}
		scrollableTarget="chatsScrollableContainer"
		scrollThreshold={0.9}
	>
		{events.map((event, index) => {
			const chatIndexOffset = chatIndexOffsets[index];
			const isLastEvent = index === events.length - 1;
			return (
				<EventAccordion
					key={event.eventId}
					event={event}
					isExpanded={expandedEvents.has(event.eventId)}
					onToggle={onAccordionToggle}
					selectedChatId={selectedChatId}
					onChatClick={onChatClick}
					chatIndexOffset={chatIndexOffset}
					isLastEvent={isLastEvent}
					sdk={null}
					eventIndex={index}
					{...menuProps}
				/>
			);
		})}
	</InfiniteScroll>
);

const RenderAdminChat = ({
	adminVendorChatRecord,
	isMenuOpen,
	onOpenMenu,
}: {
	adminVendorChatRecord: IAdminVendorChatDetails;
	isMenuOpen?: boolean;
	onOpenMenu?: (anchor: ChatMenuAnchor) => void;
}) => {
	const dispatch = useAppDispatch();
	const isSelected = window?.location?.pathname === ROUTE_PATHS.ADMIN_VENDOR_CHAT;
	const channelUrl = adminVendorChatRecord?.supportChannelUrl || "";

	const { stores } = useSendbirdStateContext();

	const sdk = stores?.sdkStore?.sdk || null;
	const lastMessageRef = useRef<{ [url: string]: number }>({});
	const adminRecordRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const scheduleAdminRecordRefresh = useCallback(() => {
		scheduleDebounced(adminRecordRefreshTimeoutRef, () => {
			void dispatch(getAdminVendorChatRecordThunk());
		});
	}, [dispatch]);

	// 1️⃣ Attach GroupChannelHandler once for real-time updates
	useEffect(() => {
		if (!sdk || isObjectEmpty(sdk)) return;

		const handler = new GroupChannelHandler({
			onMessageReceived: async (channel, message) => {
				if (channel.url !== channelUrl) return;
				lastMessageRef.current[channel.url] = message?.messageId;
				if (channelUrl && isSelected) {
					const channelFromQuery = await sdk.groupChannel.getChannel(channelUrl);
					await channelFromQuery.markAsRead();
					scheduleAdminRecordRefresh();
				}
			},
			onMessageUpdated: (channel, message) => {
				if (channel.url !== channelUrl) return;
				lastMessageRef.current[channel.url] = message.messageId;
			},
			onMessageDeleted: async (channel) => {
				const chUrl = getGroupChannelUrl(channel);
				if (!chUrl || chUrl !== channelUrl) return;

				const query = sdk.groupChannel.createMyGroupChannelListQuery({
					channelUrlsFilter: [chUrl],
					limit: 1,
					includeEmpty: true,
				});

				const channels = await query.next();
				const newLastMessage = channels[0].lastMessage;
				lastMessageRef.current[chUrl] = newLastMessage?.messageId || 0;
			},
			onChannelChanged: async (channel) => {
				if (channel.url !== channelUrl) return;
				try {
					const lastMessage = getGroupChannelLastMessage(channel);
					const lastMessageIdOriginal = lastMessage?.messageId;
					if (!lastMessageIdOriginal) return;

					const refId = lastMessageRef.current[channel.url];
					if (refId != null && String(refId) === String(lastMessageIdOriginal)) {
						return;
					}

					scheduleAdminRecordRefresh();
				} catch (error) {
					console.error("Error in onChannelChanged: ", error);
				}
			},
		});

		sdk.groupChannel?.addGroupChannelHandler("LIST_HANDLER_ADMIN_CHAT", handler);

		return () => {
			sdk.groupChannel?.removeGroupChannelHandler("LIST_HANDLER_ADMIN_CHAT");
			clearScheduledDebounced(adminRecordRefreshTimeoutRef);
		};
	}, [sdk, channelUrl, isSelected, scheduleAdminRecordRefresh]);

	return (
		<AdminVendorChatItem
			handleSelect={() => {
				lastMessageRef.current = {};
			}}
			isMenuOpen={isMenuOpen}
			onOpenMenu={onOpenMenu}
		/>
	);
};

const RenderList = ({
	chatIndexOffsets,
	handleNext,
	events,
	hasMore,
	expandedEvents,
	onAccordionToggle,
	selectedChatId,
	onChatClick,
	handleGetUnreadChannelCount,
	onSendbirdHandledUpdate,
	...menuProps
}: ListMenuProps & {
	events: IChatsRecord[];
	chatIndexOffsets: number[];
	hasMore: boolean;
	expandedEvents: Set<string>;
	selectedChatId?: string;
	onAccordionToggle: (eventId: string) => void;
	handleNext: () => void;
	onChatClick: (eventId: string, chatId: string) => void;
	handleGetUnreadChannelCount: (fetchChatsRecord?: boolean) => void;
  onSendbirdHandledUpdate?: () => void;
}) => {
	const { userDetails } = useUserInfo();
	const myUserId = userDetails?.sendbirdId;
	const loggedInVendorName = getLoggedInVendorDisplayName(userDetails);
	const [updatedEvents, setUpdatedEvents] = useState<IChatsRecord[]>(events || []);
	const previousEventsRef = useRef<IChatsRecord[]>(events || []);
	const mentionFlagsRef = useRef<Record<string, boolean>>({});
	const { stores } = useSendbirdStateContext();
	const sdk = stores?.sdkStore?.sdk || null;
	const lastMessageRef = useRef<{ [url: string]: number }>({});

	const channelMapRef = useRef<
		Map<
			string,
			{
				lastMessage: string | null;
				lastMessageTime: string | null;
				lastMessageId: string | null;
			}
		>
	>(new Map());
	const eventChannelUrlsRef = useRef<Set<string>>(new Set());

	const selectedChannelUrl = useMemo(() => {
		if (!selectedChatId) return undefined;
		for (const event of updatedEvents) {
			const chat = event.chats.find((item) => item.id === selectedChatId);
			if (chat?.channelUrl) return chat.channelUrl;
		}
		return undefined;
	}, [selectedChatId, updatedEvents]);

	const onSendbirdHandledUpdateRef = useRef(onSendbirdHandledUpdate);
	onSendbirdHandledUpdateRef.current = onSendbirdHandledUpdate;

	const myUserIdRef = useRef(myUserId);
	myUserIdRef.current = myUserId;

	const loggedInVendorNameRef = useRef(loggedInVendorName);
	loggedInVendorNameRef.current = loggedInVendorName;

	const handleGetUnreadChannelCountRef = useRef(handleGetUnreadChannelCount);
	handleGetUnreadChannelCountRef.current = handleGetUnreadChannelCount;

	const selectedChatIdRef = useRef(selectedChatId);
	selectedChatIdRef.current = selectedChatId;

	const isArchivedRef = useRef(menuProps.archived);
	isArchivedRef.current = menuProps.archived;

	useEffect(() => {
		eventChannelUrlsRef.current = new Set(updatedEvents.flatMap((e) => e.chats.map((c) => c.channelUrl)));
	}, [updatedEvents]);

	const handleChatClick = async (eventId: string, chatId: string) => {
		lastMessageRef.current = {};
		setUpdatedEvents((prev) =>
			prev.map((event) => ({
				...event,
				chats: event.chats.map((chat) => {
					if (chat.id === chatId && chat.channelUrl) {
						mentionFlagsRef.current[chat.channelUrl] = false;
						return { ...chat, hasUnreadMention: false, unreadMessageCount: 0 };
					}
					return chat;
				}),
			})),
		);
		onChatClick(eventId, chatId);
	};

	// 1️⃣ Attach GroupChannelHandler once for real-time updates
	useEffect(() => {
		if (!sdk || isObjectEmpty(sdk)) return;

		const handler = new GroupChannelHandler({
			onMessageReceived: async (channel, message) => {
				onSendbirdHandledUpdateRef.current?.();
				if (!eventChannelUrlsRef.current.has(channel.url)) return;
				lastMessageRef.current[channel.url] = message?.messageId;
				try {
					const myUserId = myUserIdRef.current;
					const loggedInVendorName = loggedInVendorNameRef.current;
					const selectedChatId = selectedChatIdRef.current;
					// To get unread message count
					const query = sdk.groupChannel.createMyGroupChannelListQuery({
						channelUrlsFilter: [channel.url], // use channelUrlsFilter, not channelUrls
						limit: 1,
						includeEmpty: true,
					});

					const channels = await query.next();
					const channelData = channels[0];
					const unreadMessageCount = channelData?.unreadMessageCount || 0;
					const mentionCount = getChannelUnreadMentionCount(channelData);

					const lastMessage = message;
					let selectedChatUrl: string | null = null;

					setUpdatedEvents((prev) => {
						const updated = prev.map((event) => ({
							...event,
							chats: event.chats.map((chat) => {
								if (chat.channelUrl === channel.url) {
									const lastMessageText = getSendbirdInboxSnippet(lastMessage, {
										isNewModelChannel: chat.isNewModelChannel,
										currentUserId: myUserId,
										loggedInVendorName,
									});
									channelMapRef.current.set(channel.url, {
										lastMessage: lastMessageText || "",
										lastMessageTime: lastMessage.createdAt
											? formatMessageTimestamp(new Date(lastMessage.createdAt).toISOString())
											: null,
										lastMessageId: lastMessage.messageId?.toString() || null,
									});
									if (selectedChatId === chat?.id) {
										selectedChatUrl = chat.channelUrl ?? null;
									}
									return {
										...chat,
										lastMessage: lastMessageText || "",
										lastMessageAt: lastMessage.createdAt || chat.lastMessageAt,
										lastMessageTime: lastMessage.createdAt
											? formatMessageTimestamp(new Date(lastMessage.createdAt).toISOString())
											: null,
										lastMessageId: lastMessage.messageId ? parseInt(String(lastMessage.messageId)) : null,
										unreadMessageCount: selectedChatId === chat?.id ? 0 : unreadMessageCount,
										hasUnreadMention: selectedChatId === chat?.id ? false : mentionCount > 0,
									};
								}
								return chat;
							}),
						}));
						return isArchivedRef.current ? updated : moveChannelEventToTop(updated, channel.url);
					});
					if (channel.url) {
						mentionFlagsRef.current[channel.url] = selectedChannelUrl === channel.url ? false : mentionCount > 0;
					}
					if (selectedChatUrl) {
						const activeChannel = await sdk.groupChannel.getChannel(selectedChatUrl);
						await activeChannel.markAsRead();
					}
				} catch (error) {
					console.error("Error in onMessageReceived: ", error);
				}
			},
			onMessageUpdated: (channel, message) => {
				onSendbirdHandledUpdateRef.current?.();
				if (!eventChannelUrlsRef.current.has(channel.url)) return;
				lastMessageRef.current[channel.url] = message.messageId;
				const lastMessage = message;
				const myUserId = myUserIdRef.current;
				const loggedInVendorName = loggedInVendorNameRef.current;
				setUpdatedEvents((prev) =>
					prev.map((event) => {
						let touched = false;

						const chats = event.chats.map((chat) => {
							if (chat.channelUrl === channel.url) {
								touched = true;
								const lastMessageText = getSendbirdInboxSnippet(lastMessage, {
									isNewModelChannel: chat.isNewModelChannel,
									currentUserId: myUserId,
									loggedInVendorName,
								});
								channelMapRef.current.set(channel.url, {
									lastMessage: lastMessageText || "",
									lastMessageTime: lastMessage?.createdAt
										? formatMessageTimestamp(new Date(lastMessage.createdAt).toISOString())
										: null,
									lastMessageId: lastMessage?.messageId?.toString() || null,
								});
								return {
									...chat,
									lastMessage: lastMessageText || "",
									lastMessageAt: lastMessage?.createdAt || chat.lastMessageAt,
									lastMessageTime: lastMessage?.createdAt
										? formatMessageTimestamp(new Date(lastMessage.createdAt).toISOString())
										: null,
									lastMessageId: lastMessage?.messageId ? Number(lastMessage.messageId) : null,
									unreadMessageCount: chat.unreadMessageCount || 0,
								};
							}
							return chat;
						});

						return touched ? { ...event, chats } : event;
					}),
				);
			},
			onMessageDeleted: async (channel) => {
				onSendbirdHandledUpdateRef.current?.();
				const chUrl = getGroupChannelUrl(channel);
				if (!chUrl || !eventChannelUrlsRef.current.has(chUrl)) return;

				const myUserId = myUserIdRef.current;
				const loggedInVendorName = loggedInVendorNameRef.current;
				const selectedChatId = selectedChatIdRef.current;

				const query = sdk.groupChannel.createMyGroupChannelListQuery({
					channelUrlsFilter: [chUrl],
					limit: 1,
					includeEmpty: true,
				});

				const channels = await query.next();
				const newLastMessage = channels[0].lastMessage;
				lastMessageRef.current[chUrl] = newLastMessage?.messageId || 0;
				const unreadMessageCount = channels[0]?.unreadMessageCount || 0;
				const mentionCount = getChannelUnreadMentionCount(channels[0]);

				setUpdatedEvents((prev) =>
					prev.map((event) => {
						let changed = false;

						const chats = event.chats.map((chat) => {
							if (chat.channelUrl === channel.url) {
								changed = true;
								const lastMessageText = getSendbirdInboxSnippet(newLastMessage, {
									isNewModelChannel: chat.isNewModelChannel,
									currentUserId: myUserId,
									loggedInVendorName,
								});
								const newData = {
									lastMessage: lastMessageText || "",
									lastMessageTime: newLastMessage?.createdAt
										? formatMessageTimestamp(new Date(newLastMessage.createdAt).toISOString())
										: null,
									lastMessageId: newLastMessage?.messageId ? newLastMessage.messageId.toString() : null,
								};
								channelMapRef.current.set(channel.url, newData);
								return {
									...chat,
									lastMessage: newData.lastMessage,
									lastMessageAt: newLastMessage?.createdAt ?? chat.lastMessageAt,
									lastMessageTime: newData.lastMessageTime,
									lastMessageId: newData.lastMessageId ? Number(newData.lastMessageId) : null,
									unreadMessageCount,
									hasUnreadMention: selectedChatId === chat.id ? false : mentionCount > 0,
								};
							}
							return chat;
						});

						return changed ? { ...event, chats } : event;
					}),
				);
			},
			onChannelChanged: async (channel) => {
				onSendbirdHandledUpdateRef.current?.();
				try {
					const lastMessage = getGroupChannelLastMessage(channel);
					const lastMessageIdOriginal = lastMessage?.messageId;

					if (!lastMessageIdOriginal) return;

					// Only run when new selected chat's message is read, not deletions/updates/receive
					if (lastMessageRef.current[channel.url] === lastMessageIdOriginal) {
						// This change is due to the last new message we already processed
						return;
					}

					const myUserId = myUserIdRef.current;
					const selectedChatId = selectedChatIdRef.current;

					const lastSenderId = getMessageSenderUserId(lastMessage);

					// Ignore if last message is sent by self
					if (lastSenderId === myUserId) return;

					// To get unread message count (await first, then use functional setState so we don't overwrite with stale closure)
					const query = sdk.groupChannel.createMyGroupChannelListQuery({
						channelUrlsFilter: [channel.url],
						limit: 1,
						includeEmpty: true,
					});

					const channels = await query.next();
					const channelData = channels[0];
					const unreadMessageCount = channelData?.unreadMessageCount || 0;

					let shouldFetchList = false;
					let listIsEmpty = false;

					// This handler behaves like an event listener (Sendbird-style).
					// It runs outside React render flow, so ALL state-dependent decisions
					// must be derived inside the setState callback.
					setUpdatedEvents((prev) => {
						// Check if the current list is empty.
						// If empty, we must fetch the full list.
						listIsEmpty = prev.length === 0;

						// Check whether the updated channel already exists
						// in the currently visible events list.
						const isChannelInList = prev.some((event) =>
							event.chats.some((chat) => chat.channelUrl === channel.url),
						);

						// Decide whether we need to fetch the full list.
						// Fetch ONLY when:
						// 1. The list is empty (initial load or cleared state)
						// 2. The channel is NOT part of the current list
						//    (means it belongs to a different event or newly activated)
						if (!isChannelInList || listIsEmpty) {
							shouldFetchList = true;
						}

						// Find the event that contains the currently selected chat.
						const selectedEvent = prev.find((event) =>
							event.chats.some((chat) => chat.id === selectedChatId),
						);

						// If no selected chat exists, do nothing.
						if (!selectedEvent) return prev;

						const selectedChatChannelUrl = selectedEvent.chats.find(
							(chat) => chat.id === selectedChatId,
						)?.channelUrl;

						// If the update does not belong to the active chat,
						// we do not modify state here.
						if (channel.url !== selectedChatChannelUrl) return prev;

						// Update unread message count ONLY for the matching channel.
						// Other events and chats remain untouched.
						return prev.map((event) => ({
							...event,
							chats: event.chats.map((chat) =>
								chat.channelUrl === channel.url ? { ...chat, unreadMessageCount } : chat,
							),
						}));
					});

					// Fetch the list ONLY when required.
					// This avoids unnecessary API calls and Firestore/Sendbird reads.
					if (shouldFetchList) {
						// listIsEmpty tells the API whether this is
						// an initial load or a partial refresh.
						handleGetUnreadChannelCountRef.current(listIsEmpty);
					}
				} catch (error) {
					console.error("Error in onChannelChanged: ", error);
				}
			},
		});

		sdk.groupChannel?.addGroupChannelHandler("LIST_HANDLER", handler);

		return () => {
			sdk.groupChannel?.removeGroupChannelHandler("LIST_HANDLER");
		};
	}, [sdk, selectedChannelUrl]);

	// Batch-fetch mention flags when list loads or selection changes
	useEffect(() => {
		if (!sdk || isObjectEmpty(sdk) || !events?.length) return;

		let cancelled = false;
		const channelUrls = events.flatMap((event) => event.chats.map((chat) => chat.channelUrl)).filter(Boolean);

		void fetchChannelMentionFlags(sdk, channelUrls, selectedChannelUrl).then((flags) => {
			if (cancelled) return;
			mentionFlagsRef.current = { ...mentionFlagsRef.current, ...flags };
			setUpdatedEvents((prev) => applyMentionFlagsToChatsRecord(prev, mentionFlagsRef.current));
		});

		return () => {
			cancelled = true;
		};
	}, [sdk, events, selectedChannelUrl]);

	// 2️⃣ Process backend data and cache it
	useEffect(() => {
		const previousEvents = previousEventsRef.current;
		previousEventsRef.current = events;
		if (!events?.length) {
			setUpdatedEvents([]);
			return;
		}
		setUpdatedEvents((prev) =>
			applyMentionFlagsToChatsRecord(
				clearSelectedChatUnread(
					mergeApiEventsWithLocalPreviews(events, prev, previousEvents, !isArchivedRef.current),
					selectedChatIdRef.current,
				),
				mentionFlagsRef.current,
			),
		);
	}, [events]);

	useEffect(() => {
		setUpdatedEvents((prev) => clearSelectedChatUnread(prev, selectedChatId));
	}, [selectedChatId]);

	return (
		<InfiniteScroll
			dataLength={updatedEvents.length}
			next={handleNext}
			hasMore={hasMore}
			loader={<EventChatsSkeleton events={1} />}
			scrollableTarget="chatsScrollableContainer"
			scrollThreshold={0.9}
		>
			{updatedEvents.map((event, index) => {
				const chatIndexOffset = chatIndexOffsets[index];
				const isLastEvent = index === events.length - 1;
				return (
					<EventAccordion
						key={event.eventId}
						event={event}
						isExpanded={expandedEvents.has(event.eventId)}
						onToggle={onAccordionToggle}
						selectedChatId={selectedChatId}
						onChatClick={handleChatClick}
						chatIndexOffset={chatIndexOffset}
						isLastEvent={isLastEvent}
						sdk={sdk}
						eventIndex={index}
						{...menuProps}
					/>
				);
			})}
		</InfiniteScroll>
	);
};

export default ChatsList;
